import React, { useState, useMemo } from 'react';
import { Bell, Heart, MessageCircle, Check, AtSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useWallNotifications, WallNotification } from '@/hooks/useWallNotifications';
import { useSendbackPrompts, SENDBACK_PROMPTS } from '@/hooks/useSendbackPrompts';
import { SendbackPromptCard } from './SendbackPromptCard';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

// Map notification types to reaction types for sendback prompts
const REACTION_TYPE_MAP: Record<string, string> = {
  'like': 'fire', // Default like maps to fire
  'reaction_fire': 'fire',
  'reaction_trophy': 'trophy',
  'reaction_grind': 'grind',
  'reaction_flowers': 'flowers',
  'reaction_teammate': 'teammate',
  'reaction_brain': 'brain',
  'reaction_clutch': 'clutch',
  'reaction_lockdown': 'lockdown',
  'reaction_levelup': 'levelup',
};

interface WallNotificationBellProps {
  profileId?: string;
}

export const WallNotificationBell: React.FC<WallNotificationBellProps> = ({ profileId }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead, isLoading } = useWallNotifications(profileId);
  const { shouldShowPrompt, getPrompt, dismissPrompt, completePrompt } = useSendbackPrompts(profileId);

  // Find the first unread notification that can show a sendback prompt
  const activePrompt = useMemo(() => {
    for (const notification of notifications) {
      if (!notification.is_read) {
        // Check if this notification type has a sendback prompt
        const reactionType = REACTION_TYPE_MAP[notification.type] || 
          (notification.type.startsWith('reaction_') ? notification.type.replace('reaction_', '') : null);
        
        if (reactionType && shouldShowPrompt(reactionType)) {
          const prompt = getPrompt(reactionType);
          if (prompt) {
            return {
              notificationId: notification.id,
              reactionType,
              ...prompt,
            };
          }
        }
      }
    }
    return null;
  }, [notifications, shouldShowPrompt, getPrompt]);

  const handleNotificationClick = (notification: WallNotification) => {
    markAsRead(notification.id);
    setOpen(false);
    navigate(`/social-wall?post=${notification.post_id}`);
  };

  const handlePromptAction = () => {
    if (activePrompt) {
      completePrompt(activePrompt.reactionType);
      setOpen(false);
      // Navigate to social wall with Following filter
      navigate('/social-wall?filter=following');
    }
  };

  const handlePromptDismiss = () => {
    if (activePrompt) {
      dismissPrompt(activePrompt.reactionType);
    }
  };

  const getNotificationMessage = (notification: WallNotification) => {
    const actorName = notification.actor?.name || 'Someone';
    
    // Check for reaction type in notification
    if (notification.type.startsWith('reaction_')) {
      const reactionType = notification.type.replace('reaction_', '');
      const promptConfig = SENDBACK_PROMPTS[reactionType];
      if (promptConfig) {
        return `${actorName} gave you a ${promptConfig.emoji}`;
      }
    }
    
    if (notification.type === 'like') {
      return `${actorName} gave you a 🔥`;
    }
    if (notification.type === 'mention') {
      return `${actorName} mentioned you in a ${notification.comment_id ? 'comment' : 'post'}`;
    }
    return `${actorName} commented on your post`;
  };

  const getNotificationIcon = (type: string) => {
    // Check for reaction type
    if (type.startsWith('reaction_')) {
      const reactionType = type.replace('reaction_', '');
      const promptConfig = SENDBACK_PROMPTS[reactionType];
      if (promptConfig) {
        return <span className="text-sm">{promptConfig.emoji}</span>;
      }
    }
    
    if (type === 'like') {
      return <span className="text-sm">🔥</span>;
    }
    if (type === 'mention') {
      return <AtSign className="h-4 w-4 text-primary" />;
    }
    return <MessageCircle className="h-4 w-4 text-primary" />;
  };

  if (!profileId) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="font-semibold">Notifications</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllAsRead()}
              className="text-xs"
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        {/* Sendback Prompt */}
        {activePrompt && (
          <div className="p-3 border-b bg-muted/30">
            <SendbackPromptCard
              emoji={activePrompt.emoji}
              message={activePrompt.message}
              onAction={handlePromptAction}
              onDismiss={handlePromptDismiss}
            />
          </div>
        )}
        
        <ScrollArea className="h-[300px]">
          {isLoading ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              Loading...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              No notifications yet
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "w-full p-3 flex items-start gap-3 text-left hover:bg-muted/50 transition-colors",
                    !notification.is_read && "bg-primary/5"
                  )}
                >
                  <Avatar className="h-8 w-8 flex-shrink-0">
                    <AvatarImage src={notification.actor?.avatar_url || undefined} />
                    <AvatarFallback className="text-xs">
                      {notification.actor?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {getNotificationIcon(notification.type)}
                      <p className="text-sm line-clamp-2">
                        {getNotificationMessage(notification)}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  {!notification.is_read && (
                    <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                  )}
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
